/*
 * Custom dataLayer events for GTM/GA4. Docs: docs/ANALYTICS_EVENTS.md.
 * Fire an event by tagging Liquid (data-dl-event + data-dl-* params) or via
 * the area modules below. Page context comes from snippets/analytics-events.liquid.
 */

window.dataLayer = window.dataLayer || [];
window.DSDAnalytics = window.DSDAnalytics || {};

const CONFIG = window.DSDAnalytics;
const PRODUCT_FORM_SUBMIT = 'product-form [name="add"], product-form [type="submit"]';

const elementText = (element) => (element?.textContent || '').trim().replace(/\s+/g, ' ');
// Strips sr-only/aria-hidden duplicate labels (e.g. an icon link's visible +
// visually-hidden copies of the same text) before reading the visible text.
const visibleText = (element) => {
  if (!element) return '';
  const clone = element.cloneNode(true);
  clone.querySelectorAll('.sr-only, [aria-hidden="true"]').forEach((hiddenNode) => hiddenNode.remove());
  return elementText(clone);
};
// Capture phase so state (aria-expanded, is-selected) is read pre-click
const onDocumentClick = (handler) => document.addEventListener('click', handler, true);

// Empty parameters are omitted: real data or nothing
function pushEvent(eventName, parameters = {}) {
  const payload = { event: eventName };
  for (const [parameterName, parameterValue] of Object.entries(parameters)) {
    if (parameterValue != null && parameterValue !== '') payload[parameterName] = parameterValue;
  }
  window.dataLayer.push(payload);
}

// data-dl-product-name -> product_name
function attributesToParameters(element) {
  const parameters = {};
  for (const attributeName of element.getAttributeNames()) {
    if (!attributeName.startsWith('data-dl-') || attributeName === 'data-dl-event') continue;
    parameters[attributeName.slice('data-dl-'.length).replace(/-/g, '_')] = element.getAttribute(attributeName);
  }
  return parameters;
}

/* Declarative: any [data-dl-event] element fires on click */
function initDeclarative() {
  onDocumentClick((clickEvent) => {
    const taggedElement = clickEvent.target.closest('[data-dl-event]');
    if (!taggedElement) return;
    const parameters = attributesToParameters(taggedElement);
    // accordion summaries report the state the click is moving to
    if (!('element_action' in parameters) && taggedElement.tagName === 'SUMMARY') {
      parameters.element_action = taggedElement.closest('details')?.open ? 'close' : 'open';
    }
    // no explicit name: fall back to the element's rendered heading — the theme
    // renders titles as native h-tags, .h* classed elements, or subheading spans
    if (!('element_name' in parameters)) {
      const headingElement = taggedElement.querySelector('h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5, .h6, .subheading');
      parameters.element_name = elementText(headingElement) || parameters.element_text;
    }
    pushEvent(taggedElement.getAttribute('data-dl-event'), parameters);
  });
}

/* dl_nav_interaction: header/footer clicks; tier + parent label derived from the DOM */
function initNav() {
  onDocumentClick((clickEvent) => {
    const navLink = clickEvent.target.closest('a, button[aria-controls]');
    if (!navLink) return;
    const navScope = navLink.closest('.header, [class*="header__"], .footer, [class*="footer__"]');
    if (!navScope) return;
    const isInFooter = navScope.matches('.footer, [class*="footer__"]');

    let menuListDepth = 0;
    let parentTriggerLabel = null;
    for (let ancestorElement = navLink.parentElement; ancestorElement && navScope.contains(ancestorElement); ancestorElement = ancestorElement.parentElement) {
      if (ancestorElement.matches('ul, [role="menu"]')) menuListDepth++;
      if (!parentTriggerLabel) {
        const ancestorTrigger = ancestorElement.querySelector(':scope > a, :scope > button');
        if (ancestorTrigger && ancestorTrigger !== navLink) parentTriggerLabel = elementText(ancestorTrigger) || null;
      }
    }

    let navAction = 'click';
    if (navLink.hasAttribute('aria-controls')) {
      navAction = navLink.getAttribute('aria-expanded') === 'true' ? 'close' : 'open';
    } else if (navLink.querySelector('img, picture')) {
      navAction = 'image_click';
    }

    pushEvent('dl_nav_interaction', {
      nav_location: isInFooter ? 'footer' : 'header',
      nav_action: navAction,
      nav_tier: Math.max(0, menuListDepth - 1),
      nav_parent_1: parentTriggerLabel || 'none',
      nav_breadcrumb: parentTriggerLabel,
      nav_text: visibleText(navLink) || navLink.getAttribute('aria-label'),
      link_url: navLink.getAttribute('href'),
    });
  });
}

/* dl_search_interaction / dl_search_submitted */
function initSearch() {
  let hasTrackedSearchFocus = false;
  document.addEventListener('focusin', (focusEvent) => {
    if (hasTrackedSearchFocus || !focusEvent.target.closest('input[type="search"], input[name="q"]')) return;
    hasTrackedSearchFocus = true;
    pushEvent('dl_search_interaction', { element_action: 'focus' });
  });
  onDocumentClick((clickEvent) => {
    const suggestionLink = clickEvent.target.closest('predictive-search a, .predictive-search-link');
    if (suggestionLink) pushEvent('dl_search_interaction', { element_action: 'suggestion_click', link_text: elementText(suggestionLink) });
  });
  document.addEventListener('submit', (submitEvent) => {
    const searchTermInput = submitEvent.target.querySelector('input[name="q"]');
    if (searchTermInput) pushEvent('dl_search_submitted', { search_term: searchTermInput.value });
  });
}

/* PDP: option selects, OOS click, back-in-stock, pay-options, rec cards */
function initPdp() {
  const product = CONFIG.product;
  const isProductPage = CONFIG.page?.type === 'product';
  const backInStockObservers = new WeakMap();

  document.addEventListener('variant:change', (variantChangeEvent) => {
    if (!product) return;
    const { variant, previousVariant } = variantChangeEvent.detail || {};
    if (!variant || !Array.isArray(variant.options)) return;
    let optionType = 'variant';
    let optionValue = variant.title;
    if (previousVariant && Array.isArray(previousVariant.options) && Array.isArray(product.options)) {
      const changedOptionIndexes = variant.options.reduce((indexes, currentOptionValue, optionIndex) => {
        if (currentOptionValue !== previousVariant.options[optionIndex]) indexes.push(optionIndex);
        return indexes;
      }, []);
      // Only attribute to a single option when exactly one changed — a picker
      // auto-fallback that swaps multiple options at once stays 'variant' rather
      // than being mislabeled as whichever option happened to differ first.
      if (changedOptionIndexes.length === 1) {
        const [changedOptionIndex] = changedOptionIndexes;
        optionType = product.options[changedOptionIndex] || optionType;
        optionValue = variant.options[changedOptionIndex];
      }
    }
    pushEvent('dl_pdp_option_select', {
      option_type: optionType,
      option_value: optionValue,
      product_id: product.id,
      product_name: product.name,
    });
  });

  // Recharge purchase-type toggle. The widget renders in shadow DOM, so change
  // events never surface; read the checked radio from the shadow root post-click.
  let lastPurchaseType = null;
  onDocumentClick((clickEvent) => {
    const subscriptionWidget = clickEvent.target.closest('recharge-subscription-widget');
    if (!subscriptionWidget?.shadowRoot) return;
    setTimeout(() => {
      const checkedPurchaseOption = subscriptionWidget.shadowRoot.querySelector('input[name="purchaseOption"]:checked');
      if (!checkedPurchaseOption || checkedPurchaseOption.value === lastPurchaseType) return;
      lastPurchaseType = checkedPurchaseOption.value;
      pushEvent('dl_pdp_option_select', {
        option_type: 'Purchase Type',
        option_value: checkedPurchaseOption.value === 'subscription' ? 'Subscription' : 'Buy Once',
        product_id: product?.id,
        product_name: product?.name,
      });
    });
  });

  onDocumentClick((clickEvent) => {
    // Sold-out option in the variant picker
    const soldOutOption = clickEvent.target.closest('variant-picker .is-disabled, variant-picker [disabled]');
    if (soldOutOption) {
      return pushEvent('dl_pdp_oos_click', {
        product_name: product?.name,
        element_name: 'product_option',
        element_text: elementText(soldOutOption) || soldOutOption.getAttribute('aria-label'),
      });
    }

    // BNPL "pay-options" drawer trigger (snippets/bnpl-drawer.liquid)
    if (clickEvent.target.closest('.bnpl-drawer-link')) {
      return pushEvent('dl_pdp_info_show', {
        element_type: 'modal_trigger',
        element_name: 'pay-options',
        element_action: 'open_modal',
        element_text: 'pay_options',
        product_name: product?.name,
        product_id: product?.id,
      });
    }

    // Back-in-stock: watch for the success message before firing. One observer
    // per form (keyed by the form element) so repeated submits across different
    // forms on the same page cannot stack or drop each other's fires.
    const backInStockSubmitButton = clickEvent.target.closest('back-in-stock-form .back-in-stock__submit');
    if (backInStockSubmitButton) {
      const backInStockFormElement = backInStockSubmitButton.closest('back-in-stock-form');
      const statusMessage = backInStockFormElement?.querySelector('.back-in-stock__message');
      if (!statusMessage) return;
      backInStockObservers.get(backInStockFormElement)?.disconnect();
      const backInStockObserver = new MutationObserver(() => {
        if (!statusMessage.hidden && statusMessage.classList.contains('text-success')) {
          backInStockObserver.disconnect();
          backInStockObservers.delete(backInStockFormElement);
          pushEvent('dl_pdp_oos_notify_me', {
            product_name: product?.name,
            element_name: 'oos_submitted',
            variant_id: backInStockFormElement.getAttribute('variant-id'),
          });
        }
      });
      backInStockObservers.set(backInStockFormElement, backInStockObserver);
      backInStockObserver.observe(statusMessage, { attributes: true, attributeFilter: ['class', 'hidden'] });
      return;
    }

    // Recommendation card click (non-ATC), PDP only
    if (isProductPage) {
      const recommendationLink = clickEvent.target.closest('product-recommendations a, recently-viewed-products a, related-products a');
      const recommendationCard = recommendationLink && !recommendationLink.closest('[data-dl-event]') && recommendationLink.closest('product-card, [data-product-id]');
      if (recommendationCard) {
        pushEvent('dl_pdp_rec_link', {
          product_id: recommendationCard.getAttribute('data-product-id'),
          product_name: recommendationCard.getAttribute('data-product-title'),
        });
      }
    }
  });
}

/* PLP: filter panel/select/action, sort, subnav */
function initPlp() {
  const listContext = () => CONFIG.collection?.handle || (CONFIG.page?.type === 'search' ? 'search' : 'collection');

  onDocumentClick((clickEvent) => {
    // Panel open/close
    const panelToggleButton = clickEvent.target.closest('[aria-controls*="facets"]');
    if (panelToggleButton) {
      return pushEvent('dl_plp_filter_panel_toggle', {
        panel_state: panelToggleButton.getAttribute('aria-expanded') === 'true' ? 'close' : 'open',
        list_context: listContext(),
      });
    }

    // Apply / clear / remove actions
    const filterActionType = clickEvent.target.closest('[form*="facets"][type="submit"]') ? 'apply'
      : clickEvent.target.closest('[form*="facets"][type="reset"], .facets-clear-all') ? 'clear'
      : clickEvent.target.closest('.active-facets a[href]') ? 'remove'
      : null;
    if (filterActionType) return pushEvent('dl_plp_filter_action', { element_action: filterActionType, list_context: listContext() });

    // Collection subnavigation (banner chips + toolbar quick links desktop/mobile)
    const subnavLink = clickEvent.target.closest('.collection-quick-navigation__item, .collection__toolbar-linklist a, #collection-toolbar-quick-links a');
    if (subnavLink) {
      pushEvent('dl_plp_subnav', {
        nav_location: 'plp_subnav',
        nav_action: 'click',
        nav_tier: 0,
        link_text: elementText(subnavLink),
        link_url: subnavLink.getAttribute('href'),
      });
    }
  });

  // "filter.v.availability" / "filter.v.option.size" -> "availability" / "size"
  const facetType = (facetInput) => facetInput.name.replace(/^filter\.[vp]\./, '').replace(/^option\./, '');
  // boolean switches carry value="1"; the label holds the readable name
  const facetValue = (facetInput) => facetInput.value === '1' ? elementText(facetInput.closest('label') || document.querySelector(`label[for="${facetInput.id}"]`)) : facetInput.value;

  // Individual filter value / sort change inside the facets form
  document.addEventListener('change', (changeEvent) => {
    const facetInput = changeEvent.target.closest('facets-form input[type="checkbox"], facets-form input[type="radio"]');
    if (!facetInput) return;
    if (facetInput.name === 'sort_by') {
      return pushEvent('dl_plp_sort_change', {
        list_context: listContext(),
        sort_option: facetInput.value || elementText(facetInput.closest('label')),
      });
    }
    // change fires after state updates, so checked inputs are the new active set
    const activeFilters = [...facetInput.closest('facets-form').querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked')]
      .filter((activeInput) => activeInput.name !== 'sort_by')
      .map((activeInput) => `${facetType(activeInput)}:${facetValue(activeInput)}`);
    pushEvent('dl_plp_filter_select', {
      filter_type: facetType(facetInput),
      filter_value: facetValue(facetInput),
      filter_state: facetInput.checked ? 'apply' : 'remove',
      element_action: facetInput.checked ? 'apply' : 'remove',
      filters_active: activeFilters.join(', '),
      list_context: listContext(),
    });
  });
}

/* ATC context: stash the click source, ElevarTransformFn adds it as `context`
   on Elevar's dl_add_to_cart / dl_remove_from_cart */
function initAtcContext() {
  const CONTEXT_MAX_AGE_MS = 5000;
  let pendingContext = null;
  const stashContext = (contextValue) => { pendingContext = { value: contextValue, stashedAt: Date.now() }; };

  window.DSDAnalytics.setAtcContext = stashContext;
  window.DSDAnalytics.consumeAtcContext = () => {
    if (!pendingContext || Date.now() - pendingContext.stashedAt > CONTEXT_MAX_AGE_MS) return null;
    const contextValue = pendingContext.value;
    pendingContext = null;
    return contextValue;
  };

  // Composes with any transform assigned to window.ElevarTransformFn, whether
  // it's already there or assigned later (e.g. Elevar's own script loading
  // after this one). A plain `window.ElevarTransformFn = ...` assignment can't
  // silently clobber this wrapper, because reassignment is routed through the
  // setter below and re-composed rather than replacing it outright.
  const withAtcContext = (transformFn) => (dataLayerEvent) => {
    const transformedEvent = transformFn ? transformFn(dataLayerEvent) : dataLayerEvent;
    if (transformedEvent && (transformedEvent.event === 'dl_add_to_cart' || transformedEvent.event === 'dl_remove_from_cart')) {
      const atcContext = window.DSDAnalytics.consumeAtcContext();
      if (atcContext) return { ...transformedEvent, context: atcContext };
    }
    return transformedEvent;
  };

  let composedTransform = withAtcContext(typeof window.ElevarTransformFn === 'function' ? window.ElevarTransformFn : null);
  Object.defineProperty(window, 'ElevarTransformFn', {
    configurable: true,
    get() { return composedTransform; },
    set(nextTransform) { composedTransform = withAtcContext(typeof nextTransform === 'function' ? nextTransform : null); },
  });

  onDocumentClick((clickEvent) => {
    // Explicit marker wins (e.g. PLP quick add carries data-atc-context)
    const markedElement = clickEvent.target.closest('[data-atc-context]');
    if (markedElement) return stashContext(markedElement.dataset.atcContext);

    if (clickEvent.target.closest('upsell-add-button')) return stashContext('pdp_upsell');

    // Dedicated cart remove button (distinct from qty-down per annotation)
    if (clickEvent.target.closest('.line-item__remove-button')) return stashContext('remove_button');

    // Cart line-item quantity steppers (anchors carrying the target quantity)
    const quantityStepperLink = clickEvent.target.closest('.quantity-selector--line-item a');
    if (quantityStepperLink?.getAttribute('href')) {
      const targetQuantity = Number(new URL(quantityStepperLink.getAttribute('href'), window.location.origin).searchParams.get('quantity'));
      const currentQuantity = Number(quantityStepperLink.closest('quantity-selector')?.querySelector('.quantity-selector__input')?.value);
      return stashContext(!Number.isNaN(currentQuantity) && targetQuantity < currentQuantity ? 'cart -' : 'cart +');
    }

    // Product form submits: any add-to-cart inside a <product-card> is a PLP
    // quick add (quick-buy modal or the desktop floating-button both render
    // inside it), otherwise it's the PDP's own CTA.
    const addToCartButton = clickEvent.target.closest(PRODUCT_FORM_SUBMIT);
    if (addToCartButton) {
      stashContext(addToCartButton.closest('product-card') ? 'plp_quick_add' : 'pdp_cta');
    }
  });
}

/* Bundles: Kitenzo clicks keyed off its kb-* classes and aria-labels;
   premade = prebuilt-bundle template PDP ATC */
function initBundle() {
  const productNameFromAriaLabel = (element) => {
    const ariaLabel = element.getAttribute('aria-label') || '';
    return ariaLabel.replace(/^(Add|Remove)\s+/, '').replace(/\s+(to|from) your set$/, '').trim();
  };
  const isPremadeBundlePage = /prebuilt/.test(CONFIG.page?.suffix || '');

  onDocumentClick((clickEvent) => {
    const bundleWidget = clickEvent.target.closest('[data-dsdurga-bundle]');
    if (!bundleWidget) {
      // Prebuilt-bundle PDP: normal add-to-cart doubles as premade selection
      if (isPremadeBundlePage && clickEvent.target.closest(PRODUCT_FORM_SUBMIT) && !clickEvent.target.closest('product-card')) {
        pushEvent('dl_select_premade_bundle', { bundle_name: CONFIG.product?.name });
      }
      return;
    }

    // Card +/- toggle: is-selected means the click removes
    const cardToggleButton = clickEvent.target.closest('.kb-card__add');
    if (cardToggleButton) {
      const bundleEventName = cardToggleButton.classList.contains('is-selected') ? 'dl_bundle_item_remove' : 'dl_bundle_item_add';
      return pushEvent(bundleEventName, { bundle_product: productNameFromAriaLabel(cardToggleButton) });
    }

    // Slot tracker remove
    const slotRemoveButton = clickEvent.target.closest('.kb-slot__remove');
    if (slotRemoveButton) return pushEvent('dl_bundle_item_remove', { bundle_product: productNameFromAriaLabel(slotRemoveButton) });

    // Product drawer "Add to set"
    const drawerAddButton = clickEvent.target.closest('.kb-drawer__cta');
    if (drawerAddButton) {
      const drawerProductTitle = drawerAddButton.closest('[class*="kb-drawer"]')?.querySelector('h1, h2, h3, [class*="__title"], [class*="__name"]');
      return pushEvent('dl_bundle_item_add', { bundle_product: elementText(drawerProductTitle) });
    }

    // Bundle add to cart; bundle_array read from the filled slots' labels
    const bundleAddToCartButton = clickEvent.target.closest('.kb-tracker__cta, .kb-msticky__cta:not(.kb-msticky__cta--link), .kb-msheet__cta');
    if (bundleAddToCartButton) {
      const bundledProductNames = [...bundleWidget.querySelectorAll('.kb-slot__remove')].map(productNameFromAriaLabel).filter(Boolean);
      // Elevar also fires add_to_cart for this — tag the source
      window.DSDAnalytics.setAtcContext('bundle_builder');
      pushEvent('dl_bundle_add_to_cart', { bundle_array: bundledProductNames.length ? bundledProductNames : null });
    }
  });
}

/* Third-party embeds (Monk gift, Recharge cart toggle, Spotify) */
function initEmbeds() {
  onDocumentClick((clickEvent) => {
    const giftButton = clickEvent.target.closest('#mr-div-embedded-cp-freegift_manual-drawer button, [data-gwp] button');
    if (!giftButton) return;
    pushEvent('dl_cart_free_gift', {
      element_action: giftButton.classList.contains('gwp-added') ? 'remove' : 'add',
      product_name: elementText(giftButton.closest('.mr-product-item')?.querySelector('.mr-text')),
      element_text: elementText(giftButton),
    });
  });

  // Recharge cart swap options (theme widget: li[data-selling-plan-id], empty = one-time)
  onDocumentClick((clickEvent) => {
    const swapOption = clickEvent.target.closest('.rc-cart-widget__option');
    if (!swapOption || swapOption.getAttribute('aria-selected') === 'true') return;
    const lineItem = swapOption.closest('.line-item');
    pushEvent('dl_cart_option_select', {
      option_type: 'purchase type',
      option_value: swapOption.dataset.sellingPlanId ? 'subscribe' : 'one-time',
      product_id: swapOption.dataset.productId,
      // first product link with text (the image link before it has none)
      product_name: lineItem && [...lineItem.querySelectorAll('a[href*="/products/"]')].map(elementText).find(Boolean),
      element_text: elementText(swapOption),
    });
  });

  // Spotify is a cross-origin iframe: focus entering it is the only observable signal
  let hasTrackedSpotify = false;
  window.addEventListener('blur', () => {
    if (hasTrackedSpotify) return;
    const focusedElement = document.activeElement;
    if (focusedElement?.tagName === 'IFRAME' && /spotify/i.test(focusedElement.src || '')) {
      hasTrackedSpotify = true;
      pushEvent('dl_spotify', { element_action: 'play' });
    }
  });
}

/* error_404 page view */
function init404() {
  if (!CONFIG.page?.is404) return;
  pushEvent('error_404', { page_location: window.location.href, page_referrer: document.referrer });
}

initDeclarative();
initNav();
initSearch();
initPdp();
initPlp();
initAtcContext();
initBundle();
initEmbeds();
init404();
